import React, { useState, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { Input } from "src/components/ui/input"
import { Button } from "src/components/ui/button"
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "src/components/ui/select"
import { fetchProjects } from "src/features/projects/api/projectsApi"
import { fetchMembers } from "src/features/members/api/membersApi"
import { STATUSES, MANAGER_ROLES } from "../constants"
import { SubdomainField } from "./SubdomainField"
import { QrCodePreview } from "./QrCodePreview"
import { MicrositeBuilder } from "../builder/MicrositeBuilder"
import { defaultContent, normalizeContent } from "../builder/blockSchema"

function Label({ children, required }) {
  return (
    <label className="mb-1 block text-[13px] font-medium text-slate-700">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  )
}

export function QrCampaignForm({ campaign, onSubmit, onCancel, isPending }) {
  const isEdit = !!campaign?.id
  const [form, setForm] = useState({
    label: "", subdomain: "", project_id: "", assigned_membership_id: "",
    manager_role: "", status: "draft",
  })
  const [content, setContent] = useState(defaultContent())
  const [subdomainOk, setSubdomainOk] = useState(false)
  const [projects, setProjects] = useState([])
  const [members, setMembers] = useState([])

  // Load picker options once.
  useEffect(() => {
    fetchProjects({ per_page: 100 }).then((d) => setProjects(d.projects || [])).catch(() => {})
    fetchMembers({ per_page: 100 }).then((d) => setMembers(d.memberships || [])).catch(() => {})
  }, [])

  // Hydrate from the campaign being edited.
  useEffect(() => {
    if (campaign?.id) {
      setForm({
        label: campaign.label || "",
        subdomain: campaign.subdomain || "",
        project_id: String(campaign.project?.id || ""),
        assigned_membership_id: String(campaign.assigned_to?.membership_id || ""),
        manager_role: campaign.manager_role || "",
        status: campaign.status || "draft",
      })
      setContent(normalizeContent(campaign.content))
      setSubdomainOk(true) // existing subdomain is valid by definition
    } else {
      setForm({ label: "", subdomain: "", project_id: "", assigned_membership_id: "", manager_role: "", status: "draft" })
      setContent(defaultContent())
      setSubdomainOk(false)
    }
  }, [campaign])

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }))

  // Available images come from the selected project (edit: serializer embeds
  // project_images; create/changed project: fall back to the project list row).
  const selectedProject = projects.find((p) => String(p.id) === String(form.project_id))
  const projectImages = isEdit && String(campaign.project?.id) === String(form.project_id)
    ? campaign.project_images || []
    : selectedProject?.images || []

  function handleSubmit(e) {
    e.preventDefault()
    if (!form.label.trim() || !form.project_id || !form.subdomain.trim() || !form.assigned_membership_id) return
    onSubmit({
      label: form.label.trim(),
      subdomain: form.subdomain.trim(),
      project_id: form.project_id,
      assigned_membership_id: form.assigned_membership_id,
      manager_role: form.manager_role || null,
      status: form.status,
      content,
    })
  }

  const canSubmit =
    form.label.trim() && form.project_id && form.subdomain.trim() &&
    form.assigned_membership_id && (isEdit || subdomainOk)

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
      <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
        {/* ── Campaign basics ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label required>Label</Label>
            <Input
              value={form.label}
              onChange={(e) => set("label", e.target.value)}
              placeholder="Onyx launch — Wakad"
            />
            <p className="mt-1 text-[12px] text-slate-400">Internal name for tracking this campaign.</p>
          </div>
          <div>
            <Label required>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label required>Subdomain</Label>
          <SubdomainField
            value={form.subdomain}
            onChange={(v) => set("subdomain", v)}
            excludeId={campaign?.id}
            onStatusChange={(res) => setSubdomainOk(!!res?.available)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label required>Project</Label>
            <Select value={form.project_id} onValueChange={(v) => set("project_id", v)}>
              <SelectTrigger><SelectValue placeholder="Select a project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.name}{p.code ? ` (${p.code})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label required>Assign leads to</Label>
            <Select value={form.assigned_membership_id} onValueChange={(v) => set("assigned_membership_id", v)}>
              <SelectTrigger><SelectValue placeholder="Select a member" /></SelectTrigger>
              <SelectContent>
                {members.map((m) => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    {m.user?.name || m.user?.email || `Member #${m.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[12px] text-slate-400">Leads from this campaign are auto-assigned to this member.</p>
          </div>
        </div>

        <div className="max-w-[280px]">
          <Label>Manager role</Label>
          <Select value={form.manager_role || ""} onValueChange={(v) => set("manager_role", v)}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {MANAGER_ROLES.filter((r) => r.value).map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="mt-1 text-[12px] text-slate-400">For reference; leads go to the assignee above.</p>
        </div>

        {isEdit && (
          <div>
            <p className="mb-2 text-[13px] font-medium text-slate-700">QR code & public link</p>
            <QrCodePreview campaign={campaign} />
          </div>
        )}

        {/* ── Page builder ─────────────────────────────────────────────── */}
        <div>
          <p className="mb-1 text-[13px] font-medium text-slate-700">Page content</p>
          <p className="mb-3 text-[12px] text-slate-400">
            Build the left side of the micro-site. The enquiry form is added automatically on the right.
          </p>
          <MicrositeBuilder
            content={content}
            onChange={setContent}
            images={projectImages}
            project={selectedProject || campaign?.project}
          />
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-3">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={!canSubmit || isPending} className="gap-1.5">
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isEdit ? "Save changes" : "Create campaign"}
        </Button>
      </div>
    </form>
  )
}
